import { c as _c } from "react/compiler-runtime";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { getPdfPrefetchQueryOptions } from "@/src/components/pdf-viewer/use-pdf";
import { taskQueryParams } from "@/src/components/tasks/use-task-column-filters";
import { taskFilterQueryParams } from "@/src/components/tasks/use-task-filters";
import { useDataTableFilters, useDataTableUrl } from "@/src/context/data-table-search-context";
import { usePowerMode } from "@/src/context/power-mode-context";
import { useTableSort } from "@/src/hooks/use-table-sort";
import { TASK_AGENT_RUN_CACHE_KEY, TASK_AGENT_RUN_STALE_TIME_MS } from "@/src/lib/data-fetchers/agent-fetchers";
import { TASK_CACHE_KEY, usePaginatedTasksMetadata } from "@/src/lib/data-fetchers/tasks-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { isPdfContentType } from "@/src/lib/utils/pdf";
import { filterSections } from "@/src/lib/utils/section-filters";
import { FileType } from "@/src/models/file";
import { TaskStatus, TaskType, hasIntakeAgentWorkflow, statusesNotActionable } from "@/src/models/task";
import { getTaskAgentRun } from "@/src/requests/agent-run";
import { getSectionFilterConfig } from "@/src/requests/section-filter-config";
import { getTaskById } from "@/src/requests/task";
export async function prefetchNextTaskAssets({
  queryClient,
  nextTaskDisplayId,
  prefetchDocuments = true
}: {
  queryClient: QueryClient;
  nextTaskDisplayId: string;
  prefetchDocuments?: boolean;
}) {
  const nextTask = await queryClient.fetchQuery({
    queryKey: [TASK_CACHE_KEY, nextTaskDisplayId],
    queryFn: () => getTaskById(nextTaskDisplayId),
    staleTime: 1000 * 30
  });
  if (!nextTask || nextTask.type !== TaskType.INTAKE) {
    return;
  }
  if (hasIntakeAgentWorkflow(nextTask)) {
    await queryClient.prefetchQuery({
      queryKey: [TASK_AGENT_RUN_CACHE_KEY, nextTask.id],
      queryFn: () => getTaskAgentRun(nextTask.id),
      staleTime: TASK_AGENT_RUN_STALE_TIME_MS
    });
  }
  if (!prefetchDocuments) {
    return;
  }
  const inboundFile = nextTask.files.find(file => file.type === FileType.INBOUND);
  if (!inboundFile) {
    return;
  }
  const sectionFilterConfig = await queryClient.fetchQuery({
    queryKey: ["sectionFilterConfig"],
    queryFn: getSectionFilterConfig,
    staleTime: Infinity
  });
  const section = filterSections(inboundFile, sectionFilterConfig)[0];
  if (!section) {
    return;
  }
  const path = section.intakePathToFile || section.pathToFile;
  if (!path || !isPdfContentType(section.contentType ?? inboundFile.contentType)) {
    return;
  }
  return queryClient.prefetchQuery(getPdfPrefetchQueryOptions(fileViewUrl(path)));
}

/**
 * Hook that powers task filtering and power mode navigation.
 * Filtering and sorting parameters are derived from the tasks table.
 * The paginated tasks metadata view powers this hook.
 */
export function useTasksNavigation(currentTaskDisplayId) {
  const $ = _c(46);
  const router = useRouter();
  const queryClient = useQueryClient();
  const tasksUrl = useDataTableUrl("/tasks");
  const {
    isPowerModeEnabled
  } = usePowerMode();
  const taskFilters = useDataTableFilters("/tasks", taskFilterQueryParams);
  const tasksFilters = useDataTableFilters("/tasks", taskQueryParams);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      path: "/tasks",
      defaultSortBy: "createdAt",
      defaultSortOrder: "desc"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    sortBy,
    sortOrder
  } = useTableSort(t0);
  const t1 = taskFilters.showCompleted ?? taskFilterQueryParams.showCompleted.defaultValue;
  const t2 = taskFilters.filterOutsideSla ?? taskFilterQueryParams.filterOutsideSla.defaultValue;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      showCompleted: t1,
      filterOutsideSla: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = tasksFilters.status || undefined;
  const t5 = tasksFilters.type || undefined;
  const t6 = tasksFilters.assignedToUser || undefined;
  const t7 = tasksFilters.priority || undefined;
  const t8 = tasksFilters.tags || undefined;
  const t9 = tasksFilters.documentClassification || undefined;
  const t10 = tasksFilters.source || undefined;
  let t11;
  if ($[4] !== t10 || $[5] !== t4 || $[6] !== t5 || $[7] !== t6 || $[8] !== t7 || $[9] !== t8 || $[10] !== t9) {
    t11 = {
      status: t4,
      type: t5,
      assignedToUser: t6,
      priority: t7,
      tags: t8,
      documentClassification: t9,
      source: t10
    };
    $[4] = t10;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== sortBy || $[13] !== sortOrder || $[14] !== t11 || $[15] !== t3) {
    t12 = {
      filters: t3,
      columnFilters: t11,
      page: 0,
      pageSize: 500,
      sortBy,
      sortOrder
    };
    $[12] = sortBy;
    $[13] = sortOrder;
    $[14] = t11;
    $[15] = t3;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const {
    tasks
  } = usePaginatedTasksMetadata(t12);
  let filteredTasks;
  let previousTask;
  let t13;
  if ($[17] !== currentTaskDisplayId || $[18] !== tasks) {
    filteredTasks = tasks.filter(_temp);
    let t14;
    if ($[22] !== currentTaskDisplayId) {
      t14 = t_0 => t_0.displayId === currentTaskDisplayId;
      $[22] = currentTaskDisplayId;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    const currentIndex = filteredTasks.findIndex(t14);
    previousTask = currentIndex > 0 ? filteredTasks[currentIndex - 1] : null;
    t13 = currentTaskDisplayId ? filteredTasks[currentIndex + 1] ?? filteredTasks.find(task => task.displayId !== currentTaskDisplayId) : null;
    $[17] = currentTaskDisplayId;
    $[18] = tasks;
    $[19] = filteredTasks;
    $[20] = previousTask;
    $[21] = t13;
  } else {
    filteredTasks = $[19];
    previousTask = $[20];
    t13 = $[21];
  }
  const nextTask = t13;
  const t14 = previousTask?.displayId ?? null;
  const t15 = nextTask?.displayId ?? null;
  let t16;
  if ($[24] !== t14 || $[25] !== t15) {
    t16 = {
      previousTaskDisplayId: t14,
      nextTaskDisplayId: t15
    };
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const {
    previousTaskDisplayId,
    nextTaskDisplayId
  } = t16;
  let t17;
  let t18;
  if ($[27] !== nextTaskDisplayId || $[28] !== previousTaskDisplayId || $[29] !== queryClient || $[30] !== router) {
    t17 = () => {
      const neighborTaskDisplayIds = [previousTaskDisplayId, nextTaskDisplayId].filter(_temp2);
      if (neighborTaskDisplayIds.length > 0) {
        neighborTaskDisplayIds.forEach(taskDisplayId_0 => {
          router.prefetch(`/tasks/${taskDisplayId_0}`);
          prefetchNextTaskAssets({
            queryClient,
            nextTaskDisplayId: taskDisplayId_0
          }).catch(_temp3);
        });
      }
    };
    t18 = [nextTaskDisplayId, previousTaskDisplayId, queryClient, router];
    $[27] = nextTaskDisplayId;
    $[28] = previousTaskDisplayId;
    $[29] = queryClient;
    $[30] = router;
    $[31] = t17;
    $[32] = t18;
  } else {
    t17 = $[31];
    t18 = $[32];
  }
  useEffect(t17, t18);
  let t19;
  if ($[33] !== isPowerModeEnabled || $[34] !== nextTaskDisplayId || $[35] !== router || $[36] !== tasksUrl) {
    t19 = async () => {
      if (!isPowerModeEnabled) {
        return router.push(tasksUrl);
      }
      return router.push(nextTaskDisplayId ? `/tasks/${nextTaskDisplayId}` : tasksUrl);
    };
    $[33] = isPowerModeEnabled;
    $[34] = nextTaskDisplayId;
    $[35] = router;
    $[36] = tasksUrl;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const navigate = t19;
  let t20;
  if ($[38] !== isPowerModeEnabled || $[39] !== navigate) {
    t20 = status => {
      if (!isPowerModeEnabled || !statusesNotActionable({
        status
      })) {
        return;
      }
      return navigate();
    };
    $[38] = isPowerModeEnabled;
    $[39] = navigate;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const navigateAfterStatusChange = t20;
  let t21;
  if ($[41] !== filteredTasks || $[42] !== navigate || $[43] !== navigateAfterStatusChange || $[44] !== tasksUrl) {
    t21 = {
      filteredTasks,
      navigate,
      navigateAfterStatusChange,
      tasksUrl
    };
    $[41] = filteredTasks;
    $[42] = navigate;
    $[43] = navigateAfterStatusChange;
    $[44] = tasksUrl;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  return t21;
}
function _temp3() {}
function _temp2(taskDisplayId) {
  return Boolean(taskDisplayId);
}
function _temp(t) {
  return !statusesNotActionable({
    status: t.status as TaskStatus
  });
}